import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatPercent } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import { ErrorBanner, SuccessBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

const TYPE_LABELS = { inclusion: 'Inclusion', exclusion: 'Exclusion', assumption: 'Assumption' }

export default function BasisOfEstimatePage() {
  const { tenderId, revisionId } = useParams()
  const [revisionStatus, setRevisionStatus] = useState(null)
  const isFrozen = revisionStatus === 'frozen'

  useEffect(() => {
    supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
      .then(({ data }) => setRevisionStatus(data?.status ?? null))
  }, [revisionId])

  return (
    <div>
      <EstimationBreadcrumb />
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 20px' }}>
        Basis of Estimate
      </h1>
      {isFrozen && (
        <div style={{ padding: '10px 16px', background: '#F1F5F9', color: colors.textSecondary, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          🔒 This revision is frozen. Basis, scope, and tax entries are shown as they were at freeze time and cannot be edited.
        </div>
      )}

      <BasisTextSection revisionId={revisionId} isFrozen={isFrozen} />
      <div style={{ height: 24 }} />
      <ScopeSection revisionId={revisionId} isFrozen={isFrozen} />
      <div style={{ height: 24 }} />
      <TaxSection revisionId={revisionId} isFrozen={isFrozen} />
    </div>
  )
}

function BasisTextSection({ revisionId, isFrozen }) {
  const [basis, setBasis] = useState(undefined)
  const [text, setText] = useState('')
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('estimate_basis').select('*').eq('estimate_revision_id', revisionId).maybeSingle()
    if (error) setError(error.message)
    else { setBasis(data); setText(data?.basis_text || '') }
  }, [revisionId])

  useEffect(() => { load() }, [load])

  async function handleSave() {
    setError(null)
    setSuccessMsg(null)
    const { data: authData } = await supabase.auth.getUser()

    if (basis) {
      const { error } = await supabase.from('estimate_basis').update({ basis_text: text, updated_by: authData.user?.id }).eq('id', basis.id)
      if (error) setError(error.message); else { setSuccessMsg('Basis of Estimate saved.'); await load() }
    } else {
      const { error } = await supabase.from('estimate_basis').insert({ estimate_revision_id: revisionId, basis_text: text, updated_by: authData.user?.id })
      if (error) setError(error.message); else { setSuccessMsg('Basis of Estimate saved.'); await load() }
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 4px' }}>Basis of Estimate</h2>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 12px' }}>
        Which drawings, specifications, and reference information this estimate was built from.
      </p>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {basis === undefined ? (
        <SkeletonRows rows={2} cols={1} />
      ) : isFrozen ? (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, fontSize: 14, color: text ? colors.textPrimary : colors.textMuted, whiteSpace: 'pre-wrap' }}>
          {text || 'No basis text was recorded for this revision.'}
        </div>
      ) : (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16 }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder="e.g. This estimate is based on IFC drawings rev A dated 2026-08-01, structural GA and sections issued by ACME Engineering Consultants..."
            style={{ width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 6, border: `1px solid ${colors.borderStrong}`, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
          />
          <div style={{ marginTop: 10 }}>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      )}
    </section>
  )
}

function ScopeSection({ revisionId, isFrozen }) {
  const [items, setItems] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [itemType, setItemType] = useState('inclusion')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('estimate_scope_item').select('*').eq('estimate_revision_id', revisionId).order('created_at')
    if (error) setError(error.message)
    else setItems(data)
  }, [revisionId])

  useEffect(() => { load() }, [load])

  async function handleAdd(e) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)
    const { data: authData } = await supabase.auth.getUser()

    const { error } = await supabase.from('estimate_scope_item').insert({
      estimate_revision_id: revisionId, item_type: itemType, description, created_by: authData.user?.id,
    })
    if (error) setError(error.message)
    else { setSuccessMsg('Added.'); setDescription(''); await load() }
    setSubmitting(false)
  }

  async function handleDelete(id) {
    setError(null)
    const { error } = await supabase.from('estimate_scope_item').delete().eq('id', id)
    if (error) setError(error.message)
    else { setSuccessMsg('Removed.'); await load() }
  }

  const grouped = { inclusion: [], exclusion: [], assumption: [] }
  items?.forEach((i) => grouped[i.item_type]?.push(i))

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 4px' }}>Scope, Exclusions &amp; Assumptions</h2>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 12px' }}>
        What is included, what is excluded, and what assumptions this price relies on.
      </p>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {!isFrozen && (
      <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 16 }}>
        <Field label="Type">
          <Select value={itemType} onChange={(e) => setItemType(e.target.value)} style={{ width: 140 }}>
            <option value="inclusion">Inclusion</option>
            <option value="exclusion">Exclusion</option>
            <option value="assumption">Assumption</option>
          </Select>
        </Field>
        <Field label="Description">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} required style={{ width: 400 }} />
        </Field>
        <Button type="submit" disabled={submitting}>{submitting ? 'Adding…' : '+ Add'}</Button>
      </form>
      )}

      {items === null ? (
        <SkeletonRows rows={2} cols={1} />
      ) : items.length === 0 ? (
        <EmptyState title="No scope items yet" description="Document at least one inclusion, exclusion, and assumption for a defensible estimate." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {['inclusion', 'exclusion', 'assumption'].map((type) => (
            <div key={type} style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                {TYPE_LABELS[type]}s
              </div>
              {grouped[type].length === 0 && <div style={{ padding: 14, fontSize: 12, color: colors.textMuted }}>None documented.</div>}
              {grouped[type].map((item) => (
                <div key={item.id} style={{ padding: '10px 14px', fontSize: 13, borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>{item.description}</span>
                  {!isFrozen && <button onClick={() => handleDelete(item.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>✕</button>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function TaxSection({ revisionId, isFrozen }) {
  const [setting, setSetting] = useState(undefined)
  const [pctInput, setPctInput] = useState('')
  const [labelInput, setLabelInput] = useState('VAT')
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('tax_setting').select('*').eq('estimate_revision_id', revisionId).maybeSingle()
    if (error) setError(error.message)
    else setSetting(data)
  }, [revisionId])

  useEffect(() => { load() }, [load])

  async function handleSave(e) {
    e.preventDefault()
    setError(null)
    setSuccessMsg(null)

    if (setting) {
      const { error } = await supabase.from('tax_setting').update({ tax_pct: Number(pctInput), tax_label: labelInput }).eq('id', setting.id)
      if (error) setError(error.message); else { setSuccessMsg('Tax setting updated.'); await load() }
    } else {
      const { error } = await supabase.from('tax_setting').insert({ estimate_revision_id: revisionId, tax_pct: Number(pctInput), tax_label: labelInput })
      if (error) setError(error.message); else { setSuccessMsg('Tax setting saved.'); await load() }
    }
  }

  return (
    <section>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 4px' }}>Tax / VAT</h2>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 12px' }}>
        Applied on top of the Tender Price for display purposes. Confirm the applicable rate for this jurisdiction.
      </p>
      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {setting === undefined ? (
        <SkeletonRows rows={1} cols={1} />
      ) : isFrozen ? null : (
        <form onSubmit={handleSave} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8 }}>
          <Field label="Label">
            <Input defaultValue={setting?.tax_label ?? 'VAT'} onChange={(e) => setLabelInput(e.target.value)} style={{ width: 100 }} />
          </Field>
          <Field label="Rate %">
            <Input type="number" step="any" min="0" defaultValue={setting?.tax_pct ?? ''} onChange={(e) => setPctInput(e.target.value)} required style={{ width: 100 }} />
          </Field>
          <Button type="submit">{setting ? 'Update' : 'Set'}</Button>
        </form>
      )}
      {setting && (
        <p style={{ fontSize: 12, color: colors.textMuted, marginTop: 8 }}>
          Currently: {setting.tax_label} at {formatPercent(setting.tax_pct)}
        </p>
      )}
    </section>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {label}{children}
    </label>
  )
}
